import { forOwn, merge, startCase } from 'lodash-es';
import { useSlots } from 'vue';
import { RouteLocation, Router, useRoute } from 'vue-router';
import { deepMerge, getPathFromHistory, isArray, replaceUrl } from '@mts-fe/utils';
import { ComponentType } from '@mts/components/Form/src/types';
import { ROW_KEY } from '@mts/components/Table/src/const';
import { router } from '@/router';
import { getGlobalStore } from '@/store';
import type {
  ActionBtnItem,
  BaseFormSchema,
  PageMode,
  RenderTableColumn,
  SchemaActionFn,
} from '..';

export * from './componentMap';
export * from './converter';
export { mergeJsonSchema } from './helper';
export * from './parser';

/** JS字符串转函数 */
const runActionFn = <T>(functionBody = '') =>
  new Function(
    `return async function (data, context, eventKey) { ${functionBody} }`,
  )() as SchemaActionFn<T>;
/** 获取钩子函数 */
export const getActionHookFn = <T>(fn: string | SchemaActionFn<T>) =>
  typeof fn === 'string' ? runActionFn<T>(fn) : fn;

/** 刷新路由：支持修改query；默认抑制该次 remount 切换动画，可传 false 取消抑制 */
export const routerReplace = (
  newQuery: Recordable = { r: Date.now() },
  disableTransition = true,
) => {
  if (disableTransition) getGlobalStore().disableRouteTransition = true;
  const route = router.currentRoute.value;
  return router.replace({ query: deepMerge(route.query, newQuery) });
};

/** 路由返回：默认返回父级 */
export const routerBack = async () => {
  const { parentPath } = router.currentRoute.value.meta;
  if (parentPath) {
    return router.replace(getPathFromHistory(parentPath as string));
  }

  router.back();
};

/** 修改路由 */
export const mergeRoutePath = (
  router: Router,
  routePath: string,
  route?: Partial<RouteLocation>,
) => {
  if (!route) {
    return routePath;
  }
  const newRoute = merge(router.resolve(routePath), route);
  return router.resolve(newRoute).fullPath;
};

/** 获取操作跳转路由 */
export const getActionRoute = (routePath: string, record?: Recordable) => {
  return record ? replaceUrl(routePath, record) : routePath;
};
/** 解析路由上指定的方法 */
export const parseRouteMethod = (path: string) => {
  const [method] = new RegExp(`(push@|replace@|go@)`).exec(path) || [];
  return (
    method ? [path.replace(method, ''), method.replace('@', '')] : [path, method || 'push']
  ) as [string, 'push' | 'replace' | 'go'];
};

export const getGlobalParams = () => ({
  route: router.currentRoute.value,
  sysConfig: getGlobalStore().getConfig,
});

/** 根据当前路由获取PageMode */
export const getCurrentPageMode = () =>
  startCase(router.currentRoute.value.query.mode?.toString()) as PageMode;

export const getTableCacheKey = (tableId?: string, pageMode: PageMode = getCurrentPageMode()) =>
  `${tableId}_TABLE${pageMode === 'Detail' ? '#Detail' : ''}`;

/** 根据按钮类型获取PageMode */
export const getRoutePageMode = (eventType: ActionBtnItem['eventType']) => {
  switch (eventType) {
    case 'ROW_ADD':
      return 'Add';
    case 'ROW_COPY':
      return 'Copy';
    case 'ROW_EDIT':
      return 'Edit';
    case 'ROW_DETAIL':
      return 'Detail';
  }
};

/** 合并接口返回值，约定默认逻辑是替换 */
export const mergeResponse = (target: Recordable, source: Recordable, replace = true) => {
  if (replace) return { [ROW_KEY]: target[ROW_KEY], ...source };

  console.warn('接口返回值合并策略不是约定的替换逻辑，oldMerge');
  return deepMerge(target, source, { mergeUndefined: true });
};

export const useSubSlot = (prefix: string) => ({
  getSubSlotKeys: Object.keys(useSlots())
    .map((i) => (i.startsWith(prefix) ? i : ''))
    .filter((i) => !!i),
  replaceSubSlotKey: (key = '') => (key ? key.replace?.(prefix, '') : key),
});

export const IGNORE_BTN_PROPS = [
  'eventType',
  'eventApi',
  'routeUrl',
  'modelId',
  'modalId',
  'oldMerge',
  'hiddenAdd',
  'hiddenEdit',
  'hiddenDetail',
  'disabledAdd',
  'disabledEdit',
  'disabledDetail',
  'canUpdateRequired',
  'checkHidden',
  'ifBtnShow',
  'openConfirm',
  'apiParams',
  'flatParam',
  'checkSelect',
  'beforeApiFn',
  'tableActionFn',
  'afterApiFn',
  'fallbackFn',
];

/** 动态判断字段是否禁用 */
export const isDisabled = (text: unknown, record: Recordable) => {
  if (typeof text === 'string') {
    const bool = Boolean(record[text.replace('!', '')]);
    return text.startsWith('!') ? !bool : bool;
  }
  return text;
};

/** 控件比较符 */
export enum COMPARATOR {
  LIKE = 'like',
  EQ = '=',
  NEQ = '!=',
  GT = '>',
  LT = '<',
  GTE = '>=',
  LTE = '<=',
  IN = 'in',
  RANGE = 'range',
  BETWEEN = 'between',
}

/** 获取控件比较符 */
export const getComparator = (comparator?: string, def = COMPARATOR.LIKE) =>
  (comparator?.toLocaleLowerCase() as COMPARATOR) || def;

/** 控件默认比较符 */
export const getDefComparator = (eventType: ComponentType) => {
  switch (eventType) {
    case 'Select':
    case 'ApiSelect':
    case 'ApiSelectAdd':
    case 'ApiRadioGroup':
    case 'DatePicker':
      return COMPARATOR.EQ;
    case 'InputRange':
      return COMPARATOR.RANGE;
    case 'RangePicker':
    case 'QuickRangePicker':
      return COMPARATOR.BETWEEN;
    default:
      return COMPARATOR.LIKE;
  }
};

export const getTableSearch = (
  search: Recordable,
  searchType: Recordable<COMPARATOR> = {},
  searchFields: BaseFormSchema[] = [],
) =>
  forOwn(search, (value, key) => {
    // 日期时间区间类型值转换
    if (searchType[key] === COMPARATOR.BETWEEN && isArray(value)) {
      const curField = searchFields.find((i) => i.field === key);
      const keys = curField?.['rangeKeys'] || [key, key];
      search[key] = [
        { key: keys[0], value: value[0] },
        { key: keys[1], value: value[1] },
      ];
    }
  });

export const tableSearchToFormValue = (search: Recordable) =>
  forOwn(search, (value, key) => {
    // 日期时间区间类型值恢复
    if (
      isArray(value) &&
      value.length === 2 &&
      value.every((i) => Object.keys(i).length === 2 && i.key && i.value)
    ) {
      search[key] = [value[0].value, value[1].value];
    }
  });

/**
 * 将查询项作为表单字段默认值；
 * 带 clearSearch 标记时清空未在 searchInfo 中的字段默认值
 */
export const setSearchDefaultValue = (
  schemas: BaseFormSchema[] = [],
  searchInfo: Recordable = {},
) => {
  const searchKeys = Object.keys(searchInfo);
  const clearDefaultValue = router.currentRoute.value.query.clearSearch === '1';
  schemas.forEach((item) => {
    if (searchKeys.includes(item.field)) {
      item.defaultValue = searchInfo[item.field];
    } else if (clearDefaultValue) {
      item.defaultValue = void 0;
    }
  });
};

const getComparatorFnBy =
  <T extends { comparator?: COMPARATOR }>(key: string) =>
  (acc: Recordable, cur: T) =>
    cur.comparator ? { ...acc, [cur[key]?.toString()]: cur.comparator } : acc;

export const getSearchType = (schemas: BaseFormSchema[] = [], def = {}): Recordable<COMPARATOR> =>
  schemas.reduce(getComparatorFnBy('field'), def);

export const getFilterType = (columns: RenderTableColumn[], def = {}): Recordable<COMPARATOR> =>
  columns.reduce(getComparatorFnBy('dataIndex'), def);

export const getFilterInfo = (columns: RenderTableColumn[], def = {}) => {
  const filterInfo = columns
    .filter((i) => i.defaultFilteredValue)
    .reduce((acc, cur) => ({ ...acc, [cur.dataIndex!.toString()]: cur.defaultFilteredValue }), {});
  return merge(def, filterInfo);
};

/** 获取路由中的页面编码 */
export const getSchemaPageCode = (path = useRoute().fullPath, prefix = 'CF') =>
  new RegExp(`${prefix}2.{0,8}`).exec(path)?.[0] || '';

/** 是否是合法的页面编码 */
export const isValidPageCode = (pageCode: string, prefix = 'CF') =>
  new RegExp(`${prefix}2.{0,8}`).test(pageCode);

/** 平台标准tableId正则表达式 */
export const tableIdExp = /^(CF2.{0,8})\.(\d{19}|\w+)(?:_(TABLE|CARD))$/;
